import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  CircularProgress
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { sendEmailVerification } from 'firebase/auth';
import { firebaseService } from '../../services/firebase';
import { isNewUser } from '../../utils/authHelpers';
// Twitter SSO removed; keeping Google only

export default function Signup() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    company: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // const auth = getAuth(); // Not needed without Twitter SSO
  async function handleSubmit(e) {
    e.preventDefault();

    // Validate form
    if (formData.password !== formData.confirmPassword) {
      return setError('Passwords do not match');
    }

    if (formData.password.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    if (!formData.name.trim()) {
      return setError('Name is required');
    }

    try {
      setError('');
      setLoading(true);

      // Create user with email and password
      const userCredential = await firebaseService.signup(formData.email, formData.password, {
        name: formData.name,
        phone: formData.phone || '',
        company: formData.company || '',
        createdAt: new Date().toISOString(),
        role: 'user',
        onboardingComplete: false // Mark as new user needing onboarding
      });

      // Send email verification with custom branded verveailabs.com service
      try {
        const resp = await fetch('https://admin.verveailabs.com/api/auth/send-custom-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'EMAIL_VERIFICATION',
            email: formData.email,
            recipientName: formData.name,
            redirectUrl: `${window.location.origin}/profile`
          })
        });
        if (!resp.ok) throw new Error('API status ' + resp.status);
      } catch (e) {
        console.warn('Backend custom email error, falling back to Firebase SDK:', e);
        const actionCodeSettings = {
          url: `${window.location.origin}/profile`,
          handleCodeInApp: false
        };
        await sendEmailVerification(userCredential.user, actionCodeSettings);
      }

      // Redirect to profile page
      navigate('/profile');
    } catch (err) {
      setError('Failed to create an account: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  const handleGoogleSignup = async () => {
    try {
      setError('');
      setLoading(true);

      // Sign in with Google
      const userCredential = await firebaseService.googleSignIn();

      // Google accounts are automatically verified
      // Check if this is a new user (just created account)
      const searchParams = new URLSearchParams(location.search);
      const customRedirect = searchParams.get('redirect');

      if (isNewUser(userCredential.user)) {
        // New Google user - set onboarding flag and go to /start or custom redirect
        await firebaseService.updateUserProfile(userCredential.user.uid, {
          onboardingComplete: false
        });
        navigate(customRedirect || '/profile');
      } else {
        // Existing Google user - go to dashboard or custom redirect
        navigate(customRedirect || '/dashboard');
      }
    } catch (err) {
      setError('Failed to sign in with Google: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Twitter signup removed

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Sign Up
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="name"
              label="Full Name"
              type="text"
              id="name"
              autoComplete="name"
              value={formData.name}
              onChange={handleChange}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="email"
              label="Email Address"
              type="email"
              id="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              name="phone"
              label="Phone Number"
              type="tel"
              id="phone"
              autoComplete="tel"
              value={formData.phone}
              onChange={handleChange}
            />
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              name="company"
              label="Company Name"
              type="text"
              id="company"
              autoComplete="organization"
              value={formData.company}
              onChange={handleChange}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign Up'}
            </Button>
          </form>

          <Divider sx={{ my: 2 }}>OR</Divider>

          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={handleGoogleSignup}
            disabled={loading}
            startIcon={<GoogleIcon />}
            sx={{ mb: 2 }}
          >
            Sign up with Google
          </Button>

          {/* Twitter SSO removed */}

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2">
              Already have an account?{' '}
              <Link to="/login" style={{ textDecoration: 'none' }}>
                <Typography color="primary" component="span" variant="body2">
                  Sign In
                </Typography>
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
